<template>
  <v-container>
    <!-- IT-703: the shared filter the other group-mode views already use.
         Brings Response Group ID search, response-id search, projects, jobs,
         date range, department and retailer — all of which get_groups has
         accepted since IT-528; this view just never sent them. -->
    <v-row class="mb-4" align="center">
      <v-col :cols="hasProductCategories ? 9 : 12">
        <PhotoFilter
          :initial-filter="groupFilters"
          @apply-filters="setFilters($event)"
        ></PhotoFilter>
      </v-col>
      <!-- Product Category filters on question_id, which PhotoFilter has no
           concept of. Rendered only where the tenant configures options, so
           it disappears for platforms with no question-level split instead of
           showing another tenant's categories. -->
      <v-col v-if="hasProductCategories" cols="3">
        <v-select
          v-model="productCategory"
          :items="productCategoryOptions"
          item-text="text"
          item-value="value"
          label="Product Category"
          outlined
          dense
          clearable
          @change="setFilters(groupFilters)"
        ></v-select>
      </v-col>
    </v-row>

    <v-row v-if="!currentGroup">
      <v-col cols="12" class="text-center">
        <v-progress-circular
          v-if="isLoading"
          indeterminate
        ></v-progress-circular>
        <div v-else-if="sessionClosed">
          <h3>Session closed</h3>
          <p class="mt-2 mb-0">
            {{ groupsSubmitted }}
            {{ groupsSubmitted === 1 ? "group was" : "groups were" }}
            submitted. Your tags have been saved.
          </p>
          <v-btn color="primary" class="mt-4" @click="startNewSession">
            Start Tagging Again
          </v-btn>
        </div>
        <div v-else>
          <h3>No groups available for tagging</h3>
          <v-btn color="primary" class="mt-4" @click="getNextGroup">
            Check for New Groups
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <template v-if="currentGroup">
      <!-- Group Info -->
      <v-row>
        <v-col cols="12">
          <h3>
            Group: {{ currentGroup.response_group_id }} / Question:
            {{ currentGroup.question_id }}
            <span class="text-caption ml-4"
              >({{ currentGroup.photos.length }} photos)</span
            >
          </h3>
        </v-col>
      </v-row>

      <!-- Horizontal Photo Gallery -->
      <div class="photo-gallery-container">
        <div class="photo-gallery">
          <div
            v-for="photo in currentGroup.photos"
            :key="photo.id"
            class="photo-item"
          >
            <img
              :src="photo.image_url"
              class="photo-image"
              @click="openLightbox(photo)"
            />
            <div class="photo-id text-caption text-center">
              ID: {{ photo.response_id }}
            </div>
            <!-- Read-only client context (FA question custom_data). Shown only
                 where the tenant opts in (MVP: Family Dollar). -->
            <div
              v-if="isPhotoNotesDisplayEnabled && photo.notes"
              class="photo-notes text-caption text-center"
            >
              {{ photo.notes }}
            </div>
          </div>
        </div>
      </div>

      <!-- Tag Selection -->
      <v-row class="border--primary mt-4">
        <v-col cols="6">
          <v-row align="end">
            <v-col cols="6" align-self="end">
              <v-autocomplete
                v-model="selectedCategory"
                :items="allCats"
                :value="allCats[0].id"
                class="mt-4"
                item-text="name"
                item-value="id"
                label="Category"
              />
            </v-col>

            <v-col cols="6">
              <v-autocomplete
                v-model="selectedTags"
                :items="filteredTags"
                :search-input.sync="tagSearchInput"
                chips
                class="mt-4"
                item-text="name"
                item-value="id"
                label="Tags"
                multiple
                return-object
                @change="tagSearchInput = ''"
              >
                <template v-slot:selection="data">
                  <v-chip
                    v-bind="data.attrs"
                    :input-value="data.selected"
                    close
                    small
                    @click="data.select"
                    @click:close="removeTagFromList(data.item)"
                  >
                    <div style="overflow: hidden">
                      {{ data.item.name }}
                      <b v-if="data.item.category && data.item.category.id" style="font-size: 11px">
                        ({{ data.item.category.name }})
                      </b>
                    </div>
                  </v-chip>
                </template>
                <template v-slot:item="data">
                  <v-list-item-content>
                    <v-list-item-title
                      v-html="data.item.name"
                    ></v-list-item-title>
                    <v-list-item-subtitle
                      v-html="data.item.category.name"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
        </v-col>

        <v-col align-self="start" cols="5">
          <small class="text-xl-left font-weight-300 mb-3"
            >Tags for this Group</small
          >
          <v-chip-group active-class="primary--text" column>
            <v-chip
              v-for="tag in selectedTags"
              :key="tag.id"
              close
              color="primary"
              @click:close="removeTagFromList(tag)"
            >
              <div style="overflow: hidden">
                {{ tag.name }}
                <b v-if="tag.category && tag.category.id" style="font-size: 11px">
                  ({{ tag.category.name }})
                </b>
              </div>
            </v-chip>
          </v-chip-group>
        </v-col>

        <v-col align-self="center" cols="3">
          <v-btn color="primary" @click="applyTags"> Apply Tags </v-btn>
        </v-col>
        <v-col align-self="center" cols="3">
          <v-checkbox v-model="needsReview" label="Needs Review"></v-checkbox>
        </v-col>
      </v-row>

      <!-- Notes -->

      <!-- Submit Buttons -->
      <v-row class="border--primary mb-2 mt-10">
        <v-btn
          class="mr-2 float-right"
          color="success"
          :disabled="isSubmitting"
          :loading="isSubmitting"
          @click="submitGroup({ getNext: true })"
        >
          Submit & Get Next Group
        </v-btn>
        <v-btn
          class="mr-2 float-right"
          color="primary"
          :disabled="isSubmitting"
          :loading="isSubmitting"
          @click="submitGroup({ getNext: false })"
        >
          Submit & Close
        </v-btn>
      </v-row>
    </template>

    <!-- Lightbox Dialog with Magnifier -->
    <v-dialog v-model="lightboxOpen" max-width="90%">
      <v-card>
        <div v-if="lightboxPhoto" class="lightbox-image-container">
          <v-btn
            icon
            class="lightbox-nav-btn lightbox-nav-prev"
            @click="goToPrevPhoto"
            :disabled="currentPhotoIndex <= 0"
          >
            <v-icon large>mdi-chevron-left</v-icon>
          </v-btn>
          <div class="lightbox-zoom-wrapper">
            <inner-image-zoom
              :src="lightboxPhoto.image_url"
              :key="lightboxPhoto.id"
              moveType="drag"
              className="lightbox-zoom-image"
            />
          </div>
          <v-btn
            icon
            class="lightbox-nav-btn lightbox-nav-next"
            @click="goToNextPhoto"
            :disabled="
              !currentGroup ||
              currentPhotoIndex >= currentGroup.photos.length - 1
            "
          >
            <v-icon large>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
        <v-card-actions>
          <span class="text-caption ml-4">
            {{ currentPhotoIndex + 1 }} /
            {{ currentGroup ? currentGroup.photos.length : 0 }}
            (← → to navigate, Ctrl+T to tag)
          </span>
          <v-spacer></v-spacer>
          <v-btn text @click="lightboxOpen = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Quick Tag Dialog -->
    <v-dialog v-model="quickTagDialogOpen" max-width="500px" @keydown.stop>
      <v-card>
        <v-card-title>Quick Tag</v-card-title>
        <v-card-text>
          <v-autocomplete
            ref="quickTagAutocomplete"
            v-model="quickTagSelection"
            :items="visibleTags"
            :search-input.sync="quickTagSearchInput"
            item-text="name"
            item-value="id"
            label="Search and select a tag"
            return-object
            autofocus
            @keydown.enter="applyQuickTag"
          >
            <template v-slot:item="data">
              <v-list-item-content>
                <v-list-item-title v-html="data.item.name"></v-list-item-title>
                <v-list-item-subtitle
                  v-html="data.item.category.name"
                ></v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="quickTagDialogOpen = false">Cancel</v-btn>
          <v-btn color="primary" @click="applyQuickTag" :disabled="!quickTagSelection">
            Add Tag
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import PhotoFilter from "@/components/filters/PhotoFilter";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import "vue-inner-image-zoom/lib/vue-inner-image-zoom.css";
import InnerImageZoom from "vue-inner-image-zoom";

export default {
  name: "GroupTagging",
  // IT-707: these were two separate `components:` keys. The second won, so
  // PhotoFilter was never registered and the filter bar added in IT-703
  // rendered as an unknown element — nothing on the page at all.
  components: {
    PhotoFilter,
    "inner-image-zoom": InnerImageZoom,
  },
  data() {
    return {
      selectedTags: [],
      selectedCategory: 0,
      tagSearchInput: "",
      needsReview: false,
      isLoading: false,
      lightboxOpen: false,
      lightboxPhoto: null,
      productCategory: null,
      // PhotoFilter-shaped, mirroring GroupCounting.vue. Keys get_groups does
      // not read (tags, onlyUntagged, includePartiallyTagged, includeInactive,
      // country) are kept for shape-compat with the shared component.
      groupFilters: {
        fromDt: null,
        thruDt: null,
        tags: [],
        projects: [],
        jobs: [],
        onlyUntagged: true,
        includePartiallyTagged: true,
        includeInactive: false,
        respIdSearchFreeForm: null,
        rgidSearchFreeForm: null,
        country: null,
        retailer: null,
        department: null,
      },
      // IT-695: guards a second click while a submit is in flight. Without it,
      // a tagger who sees nothing happen clicks again and fires a second
      // tag_group + submit_assigned pair.
      isSubmitting: false,
      // Distinguishes "you closed your session" from "there is genuinely
      // nothing left to tag" — the view previously rendered the latter for
      // both, which reads as failure after a successful submit.
      sessionClosed: false,
      groupsSubmitted: 0,
      quickTagDialogOpen: false,
      quickTagSelection: null,
      quickTagSearchInput: "",
    };
  },
  mounted() {
    this.getNextGroup();
  },
  computed: {
    ...mapState({
      currentGroup: (state) => state.photoGroups.currentGroup,
      groups: (state) => state.photoGroups.groups,
      tags: (state) => state.tags.tags,
      categories: (state) => state.categories.categories,
      productCategoryOptions: (state) => state.settings.productCategoryOptions,
    }),
    // IT-704 (P4): the pick lists and the quick-tag dialog read the visible
    // getters so machine-owned (derived) categories/tags disappear once enabled.
    ...mapGetters([
      "visibleCategories",
      "visibleTags",
      "retainHumanTags",
      "isPhotoNotesDisplayEnabled",
    ]),
    hasProductCategories() {
      return (this.productCategoryOptions || []).length > 0;
    },
    allCats() {
      var cats = this.visibleCategories;
      cats = cats.map((cat) => ({ id: cat.id, name: cat.name }));
      cats.unshift({ id: 0, name: "All" });
      return cats;
    },
    filteredTags() {
      let categId = parseInt(this.selectedCategory);
      if (categId > 0) {
        return this.visibleTags.filter((tag) => tag.category.id === categId);
      } else {
        return this.visibleTags;
      }
    },
    currentPhotoIndex() {
      if (!this.currentGroup || !this.lightboxPhoto) return -1;
      return this.currentGroup.photos.findIndex(
        (p) => p.id === this.lightboxPhoto.id
      );
    },
  },
  watch: {
    currentGroup(newGroup, oldGroup) {
      // IT-829: a lightbox photo belongs to the group it was opened from.
      // Vuetify keeps the dialog's content rendered after its first open, so a
      // stale lightboxPhoto kept the nav buttons evaluating against a group
      // that Submit & Close had just nulled — the render threw, Vue kept the
      // old DOM, and both submit buttons spun forever over a saved submit.
      // Keyed on id: a save can re-commit the same group as a new object,
      // and that must not close a lightbox the tagger is working in.
      if (!newGroup || !oldGroup || newGroup.id !== oldGroup.id) {
        this.lightboxOpen = false;
        this.lightboxPhoto = null;
      }
      if (newGroup) {
        // Load existing tags from the group. IT-704 (P4): seed only
        // human-picked tags when derivation is on, so a resave omits the
        // derived ones for the backend to recompute (a no-op when off).
        this.selectedTags = [...this.retainHumanTags(newGroup.tags)];
        this.needsReview = newGroup.needs_review || false;
      } else {
        this.selectedTags = [];
        this.needsReview = false;
      }
    },
    lightboxOpen(isOpen) {
      if (isOpen) {
        window.addEventListener("keydown", this.handleLightboxKeydown);
      } else {
        window.removeEventListener("keydown", this.handleLightboxKeydown);
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleLightboxKeydown);
  },
  methods: {
    ...mapActions(["fetchGroups", "tagGroup", "submitAssignedGroups"]),
    ...mapMutations(["setMessage", "setCurrentGroup", "setGroups"]),

    async getNextGroup(payload = {}) {
      this.isLoading = true;
      try {
        await this.fetchGroups(payload);
      } finally {
        this.isLoading = false;
      }
    },

    removeTagFromList(tag) {
      let index = this.selectedTags.findIndex((t) => t.id === tag.id);
      if (index >= 0) this.selectedTags.splice(index, 1);
    },

    async applyTags() {
      if (!this.currentGroup) return false;

      const selectedTagIds = this.selectedTags.map((tag) => tag.id);

      const payload = {
        group_id: this.currentGroup.id,
        selected_tags: selectedTagIds,
        needs_review: this.needsReview,
      };

      try {
        await this.tagGroup(payload);
        this.setMessage({
          text: "Tags Applied Successfully",
          type: "success",
          alert: true,
        });
        return true;
      } catch (e) {
        this.setMessage({
          text: "Could not save your tags — nothing was submitted. Please try again.",
          type: "error",
          alert: true,
        });
        return false;
      }
    },

    async submitGroup(payload) {
      // IT-695: both buttons are bound to this, and neither was guarded. The
      // check and the set are both synchronous, so a second click landing
      // mid-flight cannot slip past them.
      if (this.isSubmitting) return;
      this.isSubmitting = true;

      try {
        // First apply current tags
        const tagsSaved = await this.applyTags();

        // IT-699: submitting here would release the group with nothing on it
        // and report the whole thing as finished work. applyTags has already
        // surfaced the error, so just stop — the group stays on screen and the
        // tagger can retry without losing the selection.
        if (!tagsSaved) return;

        // Then submit
        await this.submitAssignedGroups();

        this.groupsSubmitted += 1;

        this.setMessage({
          text: "Group Tagged Successfully",
          type: "success",
          alert: true,
        });

        if (payload.getNext) {
          await this.getNextGroup(this.currentFilterPayload());
        } else {
          // Landing state for Submit & Close. Set before clearing the group so
          // the template never renders the "no groups available" branch in
          // between — that message is what made a successful submit look like
          // a dead end.
          this.sessionClosed = true;
          this.setGroups([]);
          this.setCurrentGroup(null);
        }
      } catch (e) {
        // Deliberately does NOT claim the session closed or bump the counter:
        // a failed submit must not be reported as finished work.
        this.setMessage({
          text: "Could not submit this group — your tags may not have saved. Please try again.",
          type: "error",
          alert: true,
        });
      } finally {
        // In `finally` so one failed request cannot leave both buttons
        // permanently disabled with a reload as the only way out.
        this.isSubmitting = false;
      }
    },

    currentFilterPayload() {
      // question_id is this view's own axis and is not part of the shared
      // filter shape, so it is merged in rather than living in groupFilters.
      const filterPayload = { ...this.groupFilters };
      if (this.productCategory) {
        filterPayload.question_id = this.productCategory;
      }
      return filterPayload;
    },

    async setFilters(newFilter) {
      this.groupFilters = { ...this.groupFilters, ...newFilter };
      await this.getNextGroup(this.currentFilterPayload());
    },

    async startNewSession() {
      this.sessionClosed = false;
      this.groupsSubmitted = 0;
      await this.getNextGroup(this.currentFilterPayload());
    },

    openLightbox(photo) {
      this.lightboxPhoto = photo;
      this.lightboxOpen = true;
    },

    handleLightboxKeydown(event) {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.goToPrevPhoto();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        this.goToNextPhoto();
      } else if (event.key === "Escape") {
        this.lightboxOpen = false;
      } else if (event.ctrlKey && event.key === "t") {
        event.preventDefault();
        this.openQuickTagDialog();
      }
    },

    goToNextPhoto() {
      if (!this.currentGroup) return;
      const nextIndex = this.currentPhotoIndex + 1;
      if (nextIndex < this.currentGroup.photos.length) {
        this.lightboxPhoto = this.currentGroup.photos[nextIndex];
      }
    },

    goToPrevPhoto() {
      if (!this.currentGroup) return;
      const prevIndex = this.currentPhotoIndex - 1;
      if (prevIndex >= 0) {
        this.lightboxPhoto = this.currentGroup.photos[prevIndex];
      }
    },

    openQuickTagDialog() {
      this.quickTagSelection = null;
      this.quickTagSearchInput = "";
      this.quickTagDialogOpen = true;
      this.$nextTick(() => {
        if (this.$refs.quickTagAutocomplete) {
          this.$refs.quickTagAutocomplete.focus();
        }
      });
    },

    applyQuickTag() {
      if (!this.quickTagSelection) return;

      // Check if tag is already in selectedTags
      const exists = this.selectedTags.some(
        (t) => t.id === this.quickTagSelection.id
      );
      if (!exists) {
        this.selectedTags.push(this.quickTagSelection);
        this.setMessage({
          text: `Tag "${this.quickTagSelection.name}" added`,
          type: "success",
          alert: true,
        });
      } else {
        this.setMessage({
          text: `Tag "${this.quickTagSelection.name}" already selected`,
          type: "info",
          alert: true,
        });
      }

      this.quickTagDialogOpen = false;
      this.quickTagSelection = null;
      this.quickTagSearchInput = "";
    },
  },
};
</script>

<style scoped>
.border--primary {
  border: 1px solid #666;
  border-radius: 4px;
  padding: 10px;
}

.fixed {
  position: fixed !important;
  bottom: 0 !important;
  z-index: 999;
  background: white;
}

.photo-gallery-container {
  overflow-x: auto;
}

.photo-gallery {
  display: flex;
  flex-direction: row;
  gap: 0;
  padding: 0;
}

.photo-item {
  flex: 0 0 auto;
}

.photo-image {
  cursor: pointer;
  border: none;
  border-radius: 0;
  height: calc(100vh - 280px);
  width: auto;
  display: block;
}

.photo-image:hover {
  outline: 2px solid var(--v-primary-base);
}

.photo-id {
  margin-top: 0;
  color: #666;
}

.photo-notes {
  margin-top: 0;
  color: #333;
  font-weight: 500;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
}

.lightbox-image-container {
  padding: 1vh 1vw;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.lightbox-zoom-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 75vh;
  overflow: hidden;
}

.lightbox-nav-btn {
  background-color: rgba(255, 255, 255, 0.8) !important;
  margin: 0 8px;
}

.lightbox-nav-btn:hover {
  background-color: rgba(255, 255, 255, 1) !important;
}
</style>

<style>
/* Unscoped styles for inner-image-zoom component */
.lightbox-zoom-wrapper .iiz {
  display: block;
  position: relative;
}

.lightbox-zoom-wrapper .iiz__img {
  max-height: 75vh;
  width: auto;
  object-fit: contain;
  display: block;
}

.lightbox-zoom-wrapper .iiz__zoom-img {
  max-height: none;
}
</style>
